import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QuestionCreateBody from "./QuestionCreateBody";
import QuestionCreateHeader from "./QuestionCreateHeader";
import Dropdown from "../../../../parts/dropdown";

const getOptions = (t) => [
  { text: t("questionTypes.shortAnswer"), value: "short" },
  { text: t("questionTypes.scale"), value: "scale" },
  { text: t("questionTypes.checkBox"), value: "checkbox" },
  { text: t("questionTypes.multipleChoice"), value: "multiple" },
  { text: t("questionTypes.bodyweight"), value: "bodyweight" },
  { text: t("questionTypes.nutritionData"), value: "nutrition" },
  { text: t("questionTypes.progressPhoto"), value: "progress" },
  { text: t("questionTypes.dailySteps"), value: "steps" },
  { text: t("questionTypes.dailySleep"), value: "sleep" },
  { text: t("questionTypes.dailyHrv"), value: "hrv" },
  { text: t("questionTypes.bodyMeasurement"), value: "measurement" },
  { text: t("questionTypes.bodyFatPercentage"), value: "bodyFatPercentage" },
  {
    text: t("questionTypes.restingHeartRate"),
    value: "restingHeartRate",
  },
];

const FIXED_HEADER_TYPES = [
  "progress",
  "nutrition",
  "bodyweight",
  "sleep",
  "steps",
  "hrv",
  "measurement",
];

const getOptionsData = (t) => ({
  short: {
    type: "short",
    body: { description: t("questionTypeDefaults.shortDescription") },
  },
  bodyweight: {
    title: t("questionTypeDefaults.bodyweightTitle"),
    type: "bodyweight",
    body: { description: t("questionTypeDefaults.bodyweightDescription") },
  },
  nutrition: {
    title: t("questionTypeDefaults.nutritionTitle"),
    type: "nutrition",
    body: {
      description: t("questionTypeDefaults.nutritionDescription"),
    },
  },
  scale: {
    type: "scale",
    body: { labelLeft: "", labelRight: "" },
  },
  progress: {
    title: t("questionTypeDefaults.progressTitle"),
    type: "progress",
  },
  multiple: {
    type: "multiple",
    body: { type: "circle-radio", options: [] },
  },
  checkbox: {
    type: "checkbox",
    body: { type: "box-checkbox", options: [] },
  },
  steps: {
    title: t("questionTypeDefaults.stepsTitle"),
    type: "steps",
    body: { description: t("questionTypeDefaults.stepsDescription") },
  },
  sleep: {
    title: t("questionTypeDefaults.sleepTitle"),
    type: "sleep",
    body: { description: t("questionTypeDefaults.sleepDescription") },
  },
  hrv: {
    title: t("questionTypeDefaults.hrvTitle"),
    type: "hrv",
    body: { description: t("questionTypeDefaults.hrvDescription") },
  },
  measurement: {
    title: t("questionTypeDefaults.measurementTitle"),
    type: "measurement",
    body: { options: [] },
  },
  bodyFatPercentage: {
    title: t("questionTypeDefaults.bodyFatPercentageTitle"),
    type: "bodyFatPercentage",
    body: {
      description: t("questionTypeDefaults.bodyFatPercentageDescription"),
    },
  },
  restingHeartRate: {
    title: t("questionTypeDefaults.restingHeartRateTitle"),
    type: "restingHeartRate",
    body: {
      description: t("questionTypeDefaults.restingHeartRateDescription"),
    },
  },
});

const QuestionCreate = ({
  data,
  setData,
  isEdited = false,
  isOnboarding = false,
}) => {
  const { t } = useTranslation("athlete-portal");
  const options = getOptions(t);
  const [select, setSelect] = useState(data.type || "short");
  const [dropdownText, setDropdownText] = useState(
    options.find((option) => option.value === data.type)?.text ||
      t("questionTypes.shortAnswer")
  );
  const [firstEdition, setFirstEdition] = useState(isEdited);

  useEffect(() => {
    if (!firstEdition) {
      setData((previousData) => {
        const optionsData = getOptionsData(t);
        const selected = optionsData[select];
        const template = {
          ...selected,
          body: selected.body
            ? {
                ...selected.body,
                ...(Array.isArray(selected.body.options)
                  ? { options: [...selected.body.options] }
                  : {}),
              }
            : undefined,
        };
        if (
          FIXED_HEADER_TYPES.includes(previousData.type) &&
          !FIXED_HEADER_TYPES.includes(template.type)
        ) {
          template.title = "";
        }
        return { ...previousData, ...template };
      });
    }
  }, [select, firstEdition, t, setData]);

  const handleSelectChange = (option) => {
    const { value, text } = option;

    setSelect(value);
    setDropdownText(text);
    setFirstEdition(false);
  };

  const filteredOptions = isOnboarding
    ? options.filter(
        (option) => option.value !== "progress" && option.value !== "bodyweight"
      )
    : options;

  return (
    <div className="questionnaire-question">
      <div className="question-header">
        <QuestionCreateHeader data={data} setData={setData} />
        <Dropdown
          summaryText={dropdownText}
          options={filteredOptions}
          onSelectFn={(receivedOption) => {
            handleSelectChange(receivedOption);
          }}
          fullOption={true}
          overlay={true}
        />
      </div>
      <div className="question-body">
        <QuestionCreateBody data={data} setData={setData} />
      </div>
    </div>
  );
};

export default QuestionCreate;
